import { useState } from "react";
import {
  discoAngelDyingSource,
  discoAngelStillInLove,
  discoBackground,
  discoDyingSourcePhoto,
  discoDyingSourceTitle,
  discoFrameDyingSource,
  discoFrameStillInLove,
  discoStillInLovePhoto,
  discoStillInLoveTitle,
  xRodClose,
} from "../assets";
import appleMusicLogo from "../assets/logos/apple-music.svg";
import deezerLogo from "../assets/logos/deezer.svg";
import spotifyLogo from "../assets/logos/spotify.svg";
import tidalLogo from "../assets/logos/tidal.svg";
import "./DiscographyPopup.css";

type Service = "Spotify" | "Apple Music" | "Tidal" | "Deezer";
type AlbumLink = { service: Service; href: string };

/** Black vector logo for each service. */
export const LOGOS: Record<Service, string> = {
  Spotify: spotifyLogo,
  "Apple Music": appleMusicLogo,
  Tidal: tidalLogo,
  Deezer: deezerLogo,
};

/** The two framed albums and their streaming album pages, without tracking params. */
export const ALBUMS: {
  id: string;
  title: string;
  photo: string;
  frame: string;
  angel: string;
  alt: string;
  links: AlbumLink[];
}[] = [
  {
    id: "album-dying-source",
    title: discoDyingSourceTitle,
    photo: discoDyingSourcePhoto,
    frame: discoFrameDyingSource,
    angel: discoAngelDyingSource,
    alt: "A dying source, album 2026",
    links: [
      { service: "Spotify", href: "https://open.spotify.com/album/5FtNLcMLndyRVJtgMgWary" },
      {
        service: "Apple Music",
        href: "https://geo.music.apple.com/us/album/a-dying-source/6791284668",
      },
    ],
  },
  {
    id: "album-still-in-love",
    title: discoStillInLoveTitle,
    photo: discoStillInLovePhoto,
    frame: discoFrameStillInLove,
    angel: discoAngelStillInLove,
    alt: "Still in love, EP 2023",
    links: [
      { service: "Spotify", href: "https://open.spotify.com/album/2BS2yC9ht6SxdixZA7f4oQ" },
      {
        service: "Apple Music",
        href: "https://geo.music.apple.com/us/album/still-in-love-ep/1672460268",
      },
      { service: "Tidal", href: "https://tidal.com/album/278668068" },
      { service: "Deezer", href: "https://www.deezer.com/album/411085127" },
    ],
  },
];

/** Two gilded frames on one sky; a frame shows its title, its cover on hover, and its services on click. */
export function DiscographyPopup({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [listed, setListed] = useState<string | null>(null);

  function close() {
    setListed(null);
    onClose();
  }

  return (
    <div
      id="discography-popup-overlay"
      className={open ? "open" : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div id="discography-popup-box" style={{ backgroundImage: `url(${discoBackground})` }}>
        <button type="button" id="discography-close-btn" onClick={close}>
          <img src={xRodClose} alt="Stäng" />
        </button>
        {listed && (
          <button
            type="button"
            id="album-links-scrim"
            aria-label="Stäng"
            onClick={() => setListed(null)}
          />
        )}
        {ALBUMS.map(({ id, title, photo, frame, angel, alt, links }) => (
          <div key={id} className={`album unit-${id}`}>
            <button
              type="button"
              id={id}
              className={listed === id ? "cover-wrap listing" : "cover-wrap"}
              aria-expanded={listed === id}
              onClick={() => setListed(id)}
            >
              <img className="cover-title" src={title} alt={alt} />
              <img className="cover-photo" src={photo} alt="" />
            </button>
            <img className="album-frame" src={frame} alt="" />
            <img className="album-angel" src={angel} alt="" />
            {listed === id && (
              <ul id="album-links" aria-label={alt}>
                {links.map(({ service, href }) => (
                  <li key={service}>
                    <a href={href} target="_blank" rel="noopener noreferrer" data-service={service}>
                      <img className="album-link-logo" src={LOGOS[service]} alt={service} />
                      <span className="album-link-listen">Listen</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
